import { useEffect, useReducer, useRef } from 'react';
import {
  reduceGameEvent,
  initialGameState,
  type GameState,
  type GameEvent,
} from '../gameEvents.js';

type Props = {
  gameUrl: string;
  onStateChange?: (state: GameState) => void;
};

/**
 * Runs a generated game.
 *
 * Two things in here are load-bearing and must not be "tidied":
 *
 * 1. `sandbox="allow-scripts"` WITHOUT `allow-same-origin`. Together those two
 *    tokens give the frame a real origin again and void the entire sandbox.
 *    Scripts alone give it an opaque origin: no cookies, no storage, no access
 *    to this page.
 *
 * 2. The `event.source` check below. The frame's origin is opaque, so
 *    `event.origin` is the string "null" and cannot identify anyone. Comparing
 *    against this iframe's own contentWindow is the only meaningful check, and
 *    it is what stops another frame or a popup impersonating the game.
 */
export function GameFrame({ gameUrl, onStateChange }: Props) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const loadedAt = useRef<number>(Date.now());
  const [state, dispatch] = useReducer(
    (s: GameState, e: GameEvent) => reduceGameEvent(s, e),
    initialGameState,
  );

  useEffect(() => {
    loadedAt.current = Date.now();
  }, [gameUrl]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== frameRef.current?.contentWindow) return;
      dispatch({ data: event.data, msSinceLoad: Date.now() - loadedAt.current });
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => onStateChange?.(state), [state, onStateChange]);

  return (
    <div className="game-frame">
      <iframe
        ref={frameRef}
        key={gameUrl}
        src={gameUrl}
        title="Your game"
        sandbox="allow-scripts"
        allow=""
        referrerPolicy="no-referrer"
      />
      <div className="game-frame__hud">
        {state.status === 'loading' && <span className="pill">Starting…</span>}
        {state.status === 'playing' && <span className="pill">Score {state.score}</span>}
        {state.status === 'won' && <span className="pill pill--good">You won! 🎉</span>}
        {state.status === 'lost' && <span className="pill">Game over</span>}
        {state.status === 'broken' && <span className="pill pill--bad">This one broke 🔧</span>}
      </div>
    </div>
  );
}
